import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import { useInviteCandidates } from "../hooks/useInviteCandidates";
import { useInviteToParty } from "../hooks/useInviteToParty";
import { mapInviteError } from "../lib/mapInviteError";

const SEARCH_DEBOUNCE_MS = 300;
const CANDIDATES_LIMIT = 15;
const SKELETON_ROWS = 3;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function safeInviteLabel(
  displayName: string | null | undefined,
  userId: string,
): string {
  const trimmed = displayName?.trim();
  if (!trimmed || trimmed === userId.trim()) return "player";
  return trimmed;
}

/** Server-filtered recent/search candidate list with Material presentation. */
export function InvitePicker({
  partyId,
  actionsDisabled = false,
}: {
  partyId: string;
  actionsDisabled?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);
  const [invitedUserIds, setInvitedUserIds] = useState<string[]>([]);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS).trim();
  const candidatesQ = useInviteCandidates(
    partyId,
    debouncedQuery,
    CANDIDATES_LIMIT,
  );
  const invite = useInviteToParty(partyId);

  useEffect(() => {
    if (!invite.isPending) setInvitingUserId(null);
  }, [invite.isPending]);

  function handleInvite(userId: string) {
    if (invite.isPending || actionsDisabled) return;
    setInviteError(null);
    setInvitingUserId(userId);
    invite.mutate(userId, {
      onSuccess: () => setInvitedUserIds((previous) => [...previous, userId]),
      onError: (error) => setInviteError(mapInviteError(error)),
    });
  }

  const candidates = candidatesQ.data ?? [];
  const searching = debouncedQuery.length > 0;

  return (
    <Box component="section" aria-labelledby="sec-invite-picker">
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" color="primary.main">
            Invite
          </Typography>
          <Typography component="h2" variant="h5" id="sec-invite-picker">
            Invite players
          </Typography>
        </Box>

        <Box
          component="form"
          role="search"
          onSubmit={(event) => event.preventDefault()}
        >
          <TextField
            fullWidth
            size="small"
            type="search"
            placeholder="Search checked-in players by name"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            slotProps={{
              htmlInput: {
                "aria-label": "Search checked-in players by name",
              },
            }}
          />
        </Box>

        {inviteError ? <Alert severity="error">{inviteError}</Alert> : null}

        {candidatesQ.isLoading ? (
          <Stack
            spacing={1}
            role="status"
            aria-label="Loading checked-in players"
          >
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Stack
                key={index}
                direction="row"
                spacing={1.25}
                data-testid="player-skeleton"
                aria-hidden="true"
                sx={{ alignItems: "center", py: 1 }}
              >
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="45%" />
                  <Skeleton width="30%" />
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : candidatesQ.isError ? (
          <Alert severity="error">
            We couldn&apos;t load checked-in players. Please try again.
          </Alert>
        ) : candidates.length === 0 ? (
          <Typography color="text.secondary">
            {searching
              ? `No checked-in players match "${debouncedQuery}"`
              : "No one else is checked in yet."}
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {!searching ? (
              <Typography variant="caption" color="text.secondary">
                Recently active
              </Typography>
            ) : null}
            {candidates.map((player) => {
              const isInvited = invitedUserIds.includes(player.user_id);
              const isInviting =
                invite.isPending && invitingUserId === player.user_id;
              const safeLabel = safeInviteLabel(
                player.display_name,
                player.user_id,
              );
              return (
                <MaterialPlayerCard
                  key={player.user_id}
                  userId={player.user_id}
                  displayName={player.display_name ?? ""}
                  avatarUrl={player.avatar_url}
                  rating={player.rating ?? undefined}
                  actions={
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      onClick={() => handleInvite(player.user_id)}
                      disabled={
                        invite.isPending || isInvited || actionsDisabled
                      }
                      aria-busy={isInviting || undefined}
                      aria-label={
                        isInvited
                          ? `${safeLabel} invited`
                          : isInviting
                            ? `Inviting ${safeLabel}`
                            : `Invite ${safeLabel}`
                      }
                      sx={{
                        "&.Mui-disabled": { pointerEvents: "auto" },
                      }}
                    >
                      {isInvited
                        ? "Invited"
                        : isInviting
                          ? "Inviting…"
                          : "Invite"}
                    </Button>
                  }
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
